import { Eye } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

interface BrandViewsPulseChipProps {
  /**
   * Distinct brand-admin viewers in the rolling 7-day window for the current
   * brand (OKS-389). Aggregate count only — NEVER admin identities (brand
   * isolation P0). The chip renders only when `count >= 1`; the backend
   * collapses 0 → `null` so the parent normally omits this chip entirely.
   */
  count: number
  className?: string
}

/**
 * OKS-385 — Brand-views pulse chip for the W8 welcome-header (UX Spec §2).
 *
 * A neutral, non-interactive companion to the accent `<StreakChip>`: an `eye`
 * icon + count-pluralized label ("{N} brand admins viewed your profile this
 * week"). Consumes the OKS-389 `brand_views_pulse` prop count.
 *
 * Meaning is carried by TEXT, never by the icon or fill alone (a11y, UX Spec
 * §6): `aria-label` holds the full untruncated label and the `Eye` icon is
 * `aria-hidden`. Aggregate only — the copy never names an admin.
 *
 * Renders `null` at `count < 1` (defensive — the parent should already gate
 * on a non-null prop).
 *
 * # Locked testid
 *   - `brand-views-pulse-chip`
 */
export function BrandViewsPulseChip({
  count,
  className,
}: BrandViewsPulseChipProps) {
  const { t } = useTranslation()

  if (count < 1) return null

  const label = t("missions.brand_views_pulse", { count })

  return (
    <Badge
      variant="secondary"
      data-testid="brand-views-pulse-chip"
      aria-label={label}
      className={cn("max-w-full truncate", className)}
    >
      <Eye aria-hidden="true" />
      <span className="truncate">{label}</span>
    </Badge>
  )
}
